/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TranslateIcon from '@mui/icons-material/Translate';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { BookOpenText } from 'lucide-react';
import { PaperCard } from '../components/Common';
import { alpha } from '@mui/material/styles';

interface FeatureCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ title, description, icon, onClick }) => {
  return (
    <Card 
      sx={(theme) => ({ 
        backgroundColor: alpha(theme.palette.primary.main, 0.06),
        height: '100%',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        },
        boxShadow: 0,
        borderRadius: '20px'
      })}
    >
      <CardActionArea onClick={onClick} sx={{ height: '100%', p: 3 }}>
        <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Box sx={{ 
            mb: '12px', 
            color: 'primary.main',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 48
          }}>
            {icon}
          </Box>
          <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 500, fontSize: '24px', lineHeight: '32px', letterSpacing: '0px', color: 'text.primary', mb: '6px' }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '18px', lineHeight: '28px', letterSpacing: '0.2px', color: 'text.secondary' }}>
            {description}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const features = [
    {
      title: t('homePage.features.documentTranslation.title'),
      description: t('homePage.features.documentTranslation.description'),
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 48 }} />,
      onClick: () => navigate('/translate-tasks'),
    },
    {
      title: t('common.settings'),
      description: t('homePage.features.settings.description'),
      icon: <SettingsOutlinedIcon sx={{ fontSize: 48 }} />,
      onClick: () => navigate('/settings'),
    },
    {
      title: t('homePage.features.glossary.title'),
      description: t('homePage.features.glossary.description'),
      icon: <BookOpenText size={48} />,
      onClick: () => navigate('/glossary'),
    },
    {
      title: t('homePage.features.referenceStore.title'),
      description: t('homePage.features.referenceStore.description'),
      icon: <LibraryBooksOutlinedIcon sx={{ fontSize: 48 }} />,
      onClick: () => navigate('/reference-store'),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PaperCard>
        <Box sx={{ textAlign: 'center', mb: '15px' }}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: 700,
              fontSize: '40px',
              lineHeight: '48px',
              letterSpacing: '0px',
              color: 'primary.main',
              mb: '12px',
              mt: '12px',
            }}
          >
            {t('homePage.title')}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={6} key={index}>
              <FeatureCard
                title={feature.title}
                description={feature.description}
                icon={feature.icon}
                onClick={feature.onClick}
              />
            </Grid>
          ))}
        </Grid>
      </PaperCard>
    </Container>
  );
};
